import { ChevronDown, Minus } from "lucide-react";
import { type ReactNode, useState } from "react";

interface CollapsibleSessionBarProps {
  label: string;
  collapsedLabel: string;
  collapsedIcon: ReactNode;
  leading: ReactNode;
  trailing: ReactNode;
  canCollapse?: boolean;
}

export function CollapsibleSessionBar({
  label,
  collapsedLabel,
  collapsedIcon,
  leading,
  trailing,
  canCollapse = true,
}: CollapsibleSessionBarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Rows share the session header and transcript column (data-conversation-column);
  // the divider spans the column's text width, not the page.
  const divider = (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-4 bottom-0 h-px bg-white/[0.06] lg:inset-x-8"
    />
  );
  return (
    <div className="bg-[#0f0f14]" data-session-bar>
      {isCollapsed ? (
        <div
          data-conversation-column
          className="relative mx-auto flex w-full max-w-2xl justify-center px-4 py-1 lg:max-w-3xl lg:px-8 xl:max-w-4xl"
        >
          <button
            type="button"
            aria-expanded="false"
            aria-label={`Expand ${label} bar`}
            onClick={() => setIsCollapsed(false)}
            className="flex min-w-0 items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-medium text-white/35 transition-colors hover:bg-white/[0.045] hover:text-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/35"
          >
            {collapsedIcon}
            <span className="truncate">{collapsedLabel}</span>
            <ChevronDown size={12} aria-hidden="true" />
          </button>
          {divider}
        </div>
      ) : (
        <div
          data-conversation-column
          className="relative mx-auto flex w-full max-w-2xl items-center justify-between gap-3 px-4 py-2 lg:max-w-3xl lg:px-8 xl:max-w-4xl"
        >
          {leading}
          <div className="flex shrink-0 items-center gap-1">
            {trailing}
            {canCollapse && (
              <button
                type="button"
                aria-expanded="true"
                aria-label={`Minimize ${label} bar`}
                onClick={() => setIsCollapsed(true)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/25 transition-colors hover:bg-white/[0.055] hover:text-white/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/35"
              >
                <Minus size={14} aria-hidden="true" />
              </button>
            )}
          </div>
          {divider}
        </div>
      )}
    </div>
  );
}
